<script setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRoute } from "vue-router";
import AppTopbar from "@/components/AppTopbar.vue";
import SideRails from "@/components/SideRails.vue";
import OfficialNotice from "@/components/OfficialNotice.vue";
import { useLangStore } from "@/stores/lang.js";
import { useVaultsStore } from "@/stores/vaults.js";
import {
  OFFICIAL_TG_HANDLE,
  OFFICIAL_TG_URL,
  OFFICIAL_X_HANDLE,
  OFFICIAL_X_URL,
} from "@/lib/officialChannels.js";

const route = useRoute();
const lang = useLangStore();
const vaults = useVaultsStore();
const toast = ref("");
let toastTimer = 0;

const isMonitor = computed(() => route.name === "monitor");
const isMintLive = computed(() => route.name === "mint-live");
const isMarket = computed(() => route.name === "market");
const isMint = computed(
  () => route.name === "mint-create" || route.name === "mint-detail"
);
const isDeploy = computed(
  () => route.name === "deploy-bbk-v3" || route.name === "deploy-stock"
);

/** 模板里不能写 ||（会被当成 filter 管道） */
const shellIsMonitor = computed(() => isMonitor.value || isMintLive.value);
const shellIsMint = computed(() => isMint.value || isMintLive.value);
const shellIsDeploy = computed(
  () => isDeploy.value || isMint.value || isMintLive.value
);

const footer = computed(() => {
  if (isMarket.value) {
    return {
      kicker: lang.t("封心 · 市值", "Fengxin · Market"),
      ver: lang.t("BSC · 本地操作钱包", "BSC · local operator wallets"),
      body: lang.t(
        "私钥仅存本机浏览器。公共电脑勿用。列表操作不构成买卖建议。",
        "Keys stay in this browser. Not for public PCs. Not trading advice."
      ),
    };
  }
  if (isMintLive.value) {
    return {
      kicker: lang.t("封心 · Mint 监听", "Fengxin · Mint Live"),
      ver: lang.t("BSC · 独立板块 · 与工厂监听分离", "BSC · standalone · not factory monitor"),
      body: lang.t(
        "只展示 Mint 工厂金库募资进度与状态，不混入 Flap 挂载监听。不构成投资建议。",
        "Mint factory raise board only — separate from Flap attach monitor. Not investment advice."
      ),
    };
  }
  if (isMint.value) {
    return {
      kicker: lang.t("封心 · 公平 Mint", "Fengxin · Fair Mint"),
      ver: lang.t("BSC · 仅 BNB · 按份领币", "BSC · BNB only · claim by share"),
      body: lang.t(
        "独立 Mint 板块：募资 → 满额自动发射 → 领币。发射前可退款。不构成投资建议。",
        "Standalone Mint: raise → auto-launch when filled → claim. Refunds before launch. Not investment advice."
      ),
    };
  }
  if (isMonitor.value) {
    return {
      kicker: lang.t("封心 · 工厂监听", "Fengxin · Factory Monitor"),
      ver: lang.t("BSC · 实时", "BSC · Live"),
      body: lang.t(
        "只展示工厂挂载动态，不构成买卖建议。与 Mint 监听相互独立。",
        "Factory attach feed only. Separate from Mint Live. Not trading advice."
      ),
    };
  }
  if (isDeploy.value) {
    return {
      kicker: lang.t("封心 · 站内部署", "Fengxin · On-site Deploy"),
      ver: lang.t("BSC · 站内核对 · 钱包确认", "BSC · review here · confirm in wallet"),
      body: lang.t(
        "核对工厂地址、参数与费用，再在钱包中确认发射交易。",
        "Review the factory address, parameters and fees, then confirm the launch transaction in your wallet."
      ),
    };
  }
  return {
    kicker: lang.t("封心 · Flap 金库", "Fengxin · Flap Vaults"),
    ver: lang.t("BSC · 工厂地址以本页为准", "BSC · factory addresses on this page are authoritative"),
    body: lang.t(
      "现成模板可直接挂载；定制需求请联系电报。列表信息不构成投资建议。",
      "Ready templates can be mounted directly; contact TG for custom work. Listings are not investment advice."
    ),
  };
});

function showToast(msg) {
  toast.value = msg;
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    toast.value = "";
  }, 1800);
}

function onToast(e) {
  if (e?.detail) showToast(String(e.detail));
}

onMounted(() => {
  window.addEventListener("fx-toast", onToast);
  vaults.loadShowcase();
});
onUnmounted(() => {
  window.removeEventListener("fx-toast", onToast);
  clearTimeout(toastTimer);
});
</script>

<template>
  <div
    class="page-shell"
    :class="{
      'is-monitor': shellIsMonitor,
      'is-market': isMarket,
      'is-mint': shellIsMint,
      'is-mint-live': isMintLive,
      'is-deploy': shellIsDeploy,
    }"
  >
    <div class="bg-wash" aria-hidden="true" />
    <div class="bg-orbit" aria-hidden="true" />
    <div class="noise" aria-hidden="true" />
    <a class="skip-link" href="#main-content">{{ lang.t("跳到主内容", "Skip to content") }}</a>
    <AppTopbar />
    <SideRails />
    <main id="main-content" tabindex="-1">
      <RouterView />
    </main>
    <footer class="footer">
      <div class="footer-rail">
        <div class="footer-kicker">{{ footer.kicker }}</div>
        <p class="footer-ver">{{ footer.ver }}</p>
      </div>
      <p>{{ footer.body }}</p>
      <p class="footer-contact">
        <template v-if="isMarket">
          <RouterLink to="/">{{ lang.t("回金库", "Home") }}</RouterLink>
          ·
          <a :href="OFFICIAL_TG_URL" target="_blank" rel="noopener noreferrer">{{
            lang.t(`电报 ${OFFICIAL_TG_HANDLE}`, `TG ${OFFICIAL_TG_HANDLE}`)
          }}</a>
        </template>
        <template v-else>
          <a :href="OFFICIAL_X_URL" target="_blank" rel="noopener noreferrer">X {{ OFFICIAL_X_HANDLE }}</a>
          ·
          <a :href="OFFICIAL_TG_URL" target="_blank" rel="noopener noreferrer">{{
            lang.t(`电报 ${OFFICIAL_TG_HANDLE}`, `TG ${OFFICIAL_TG_HANDLE}`)
          }}</a>
        </template>
      </p>
      <p class="footer-privacy">
        {{
          lang.t(
            "本站用浏览器本地存储记住语言与界面偏好；市值工具的密钥只存在本机，不会上传。不使用广告追踪 Cookie。工厂地址以本页列表为准。",
            "Local storage for language and UI prefs. Market keys stay on this device and are never uploaded. No ad-tracking cookies. Factory addresses on this page are authoritative."
          )
        }}
      </p>
    </footer>
    <div class="toast" :class="{ show: !!toast }" role="status" aria-live="polite">{{ toast }}</div>
    <OfficialNotice />
  </div>
</template>
